import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useLocation } from 'react-router-dom';
import { toastMsg } from '../../../../components/common/Toast/Toast';
import { API } from '../../../../services/apiService';
import { getLinkedWalletAddress } from '../../../../services/wallet/linkedWallet.utils';
import { PLACE_ORDER_MARKET_MODE } from '../placeOrder.constants';
import {
  WHITELIST_POLL_INTERVAL_MS,
  WHITELIST_PENDING,
  WHITELIST_STATUS,
} from '../buySellTab.constants';
import {
  initialWhitelistUi,
  mapWhitelistStatus,
  whitelistUiReducer,
} from './whitelistUi.reducer';

export function useSpotWhitelist({
  selectedPair,
  marketMode,
  selectedSide,
  selectedTradeWallet,
  selectTradeWallet,
}) {
  const location = useLocation();
  const [whitelistUi, dispatchWhitelistUi] = useReducer(
    whitelistUiReducer,
    initialWhitelistUi,
  );

  const isSpotMode = marketMode === PLACE_ORDER_MARKET_MODE.SPOT;

  const whitelistCoin = useMemo(() => {
    const coin = selectedPair?.otherCurrency;
    if (coin == null || coin === '') return '';
    return String(coin).trim().toLowerCase();
  }, [selectedPair?.otherCurrency]);

  const isSpotTradeWhitelistRoute = useMemo(() => {
    if (!isSpotMode) return false;
    if (location.pathname !== '/trade') return false;
    return Boolean(whitelistCoin);
  }, [isSpotMode, location.pathname, whitelistCoin]);

  const applyWhitelistResponse = useCallback(
    ({ response, scope, address, showErrorToast, cancelled }) => {
      if (cancelled?.()) {
        return null;
      }

      if (response?.status != null && Number(response.status) !== 200) {
        const errorMessage =
          response?.message || 'Failed to load whitelist status.';
        dispatchWhitelistUi({
          type: scope === 'wallet' ? 'wallet_fetch_set_error' : 'pair_fetch_set_error',
          errorMessage,
          address,
        });
        if (showErrorToast) toastMsg.error(errorMessage);
        return response;
      }

      dispatchWhitelistUi({
        type: scope === 'wallet' ? 'wallet_fetch_set_status' : 'pair_fetch_set_status',
        status: mapWhitelistStatus(response),
        address,
      });
      return response;
    },
    [],
  );

  const fetchPairWhitelistStatus = useCallback(
    async ({ showLoading = false, showErrorToast = false, isCancelled } = {}) => {
      const cancelled = () => (typeof isCancelled === 'function' ? isCancelled() : false);

      if (!isSpotTradeWhitelistRoute || !whitelistCoin) {
        if (!cancelled()) dispatchWhitelistUi({ type: 'reset' });
        return null;
      }

      if (showLoading && !cancelled()) {
        dispatchWhitelistUi({ type: 'pair_fetch_start' });
      }
      try {
        const response = await API.fetchAddressWhitelist({ coin: whitelistCoin });
        if (cancelled()) {
          if (showLoading) dispatchWhitelistUi({ type: 'pair_fetch_abort' });
          return null;
        }
        return applyWhitelistResponse({
          response,
          scope: 'pair',
          showErrorToast,
          cancelled,
        });
      } catch (error) {
        if (cancelled()) {
          if (showLoading) dispatchWhitelistUi({ type: 'pair_fetch_abort' });
          return null;
        }
        const errorMessage =
          error?.response?.data?.message || 'Failed to load whitelist status.';
        dispatchWhitelistUi({ type: 'pair_fetch_set_error', errorMessage });
        if (showErrorToast) toastMsg.error(errorMessage);
        return null;
      }
    },
    [applyWhitelistResponse, isSpotTradeWhitelistRoute, whitelistCoin],
  );

  const fetchWalletWhitelistStatus = useCallback(
    async ({
      address,
      showLoading = false,
      showErrorToast = false,
      isCancelled,
    } = {}) => {
      const cancelled = () => (typeof isCancelled === 'function' ? isCancelled() : false);
      const normalizedAddress = String(address ?? '').trim();

      if (!isSpotTradeWhitelistRoute || !whitelistCoin || !normalizedAddress) {
        return null;
      }

      if (showLoading && !cancelled()) {
        dispatchWhitelistUi({ type: 'wallet_fetch_start', address: normalizedAddress });
      }
      try {
        const response = await API.fetchAddressWhitelist({
          coin: whitelistCoin,
          address: normalizedAddress,
        });
        if (cancelled()) {
          if (showLoading) dispatchWhitelistUi({ type: 'wallet_fetch_abort' });
          return null;
        }
        return applyWhitelistResponse({
          response,
          scope: 'wallet',
          address: normalizedAddress,
          showErrorToast,
          cancelled,
        });
      } catch (error) {
        if (cancelled()) {
          if (showLoading) dispatchWhitelistUi({ type: 'wallet_fetch_abort' });
          return null;
        }
        const errorMessage =
          error?.response?.data?.message || 'Failed to load whitelist status.';
        dispatchWhitelistUi({
          type: 'wallet_fetch_set_error',
          errorMessage,
          address: normalizedAddress,
        });
        if (showErrorToast) toastMsg.error(errorMessage);
        return null;
      }
    },
    [applyWhitelistResponse, isSpotTradeWhitelistRoute, whitelistCoin],
  );

  useEffect(() => {
    if (!isSpotTradeWhitelistRoute) {
      dispatchWhitelistUi({ type: 'reset' });
      return undefined;
    }

    let cancelled = false;
    fetchPairWhitelistStatus({
      showLoading: true,
      showErrorToast: true,
      isCancelled: () => cancelled,
    });

    return () => {
      cancelled = true;
    };
  }, [fetchPairWhitelistStatus, isSpotTradeWhitelistRoute]);

  useEffect(() => {
    dispatchWhitelistUi({ type: 'wallet_reset' });
  }, [whitelistCoin]);

  // Re-check wallet whitelist when a wallet is already selected (e.g. return from Portfolio).
  useEffect(() => {
    if (!isSpotTradeWhitelistRoute) return undefined;

    const address = getLinkedWalletAddress(selectedTradeWallet);
    if (!selectedTradeWallet?.walletId || !address) {
      dispatchWhitelistUi({ type: 'wallet_reset' });
      return undefined;
    }

    let cancelled = false;
    fetchWalletWhitelistStatus({
      address,
      showLoading: true,
      showErrorToast: false,
      isCancelled: () => cancelled,
    });

    return () => {
      cancelled = true;
    };
  }, [
    fetchWalletWhitelistStatus,
    isSpotTradeWhitelistRoute,
    selectedTradeWallet,
    whitelistCoin,
  ]);

  useEffect(() => {
    if (
      !isSpotTradeWhitelistRoute ||
      whitelistUi.wallet.status !== WHITELIST_STATUS.SUBMITTED ||
      !whitelistUi.wallet.address
    ) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      fetchWalletWhitelistStatus({
        address: whitelistUi.wallet.address,
        showLoading: false,
        showErrorToast: false,
      });
    }, WHITELIST_POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [
    fetchWalletWhitelistStatus,
    isSpotTradeWhitelistRoute,
    whitelistUi.wallet.address,
    whitelistUi.wallet.status,
  ]);

  const handleSelectWallet = useCallback(
    async (wallet) => {
      await selectTradeWallet(wallet);
      if (!isSpotTradeWhitelistRoute) return;
      const address = getLinkedWalletAddress(wallet);
      if (!address) return;
      await fetchWalletWhitelistStatus({
        address,
        showLoading: true,
        showErrorToast: true,
      });
    },
    [fetchWalletWhitelistStatus, isSpotTradeWhitelistRoute, selectTradeWallet],
  );

  const handleSubscribeWhitelist = useCallback(async () => {
    if (!whitelistCoin || !isSpotTradeWhitelistRoute) return;
    if (whitelistUi.pending === WHITELIST_PENDING.SUBSCRIBE) return;

    const address =
      getLinkedWalletAddress(selectedTradeWallet) ||
      String(whitelistUi.wallet.address ?? '').trim();
    if (!address) {
      toastMsg.error('Select a wallet before subscribing.');
      return;
    }

    dispatchWhitelistUi({ type: 'subscribe_start' });
    try {
      const response = await API.subscribeAddressWhitelist({
        coin: whitelistCoin,
        address,
      });
      if (response?.status === 200) {
        toastMsg.success(response?.message || 'Subscribed successfully.');
      } else {
        toastMsg.error(response?.message || 'Subscribe failed.');
      }
      await fetchWalletWhitelistStatus({
        address,
        showLoading: false,
        showErrorToast: false,
      });
    } catch (error) {
      toastMsg.error(error?.response?.data?.message || 'Failed to subscribe.');
      await fetchWalletWhitelistStatus({
        address,
        showLoading: false,
        showErrorToast: false,
      });
    } finally {
      dispatchWhitelistUi({ type: 'subscribe_end' });
    }
  }, [
    fetchWalletWhitelistStatus,
    isSpotTradeWhitelistRoute,
    selectedTradeWallet,
    whitelistCoin,
    whitelistUi.pending,
    whitelistUi.wallet.address,
  ]);

  const isPairWhitelistFetching =
    whitelistUi.pending === WHITELIST_PENDING.FETCH_PAIR;
  const isWalletWhitelistFetching =
    whitelistUi.pending === WHITELIST_PENDING.FETCH_WALLET;
  const isWhitelistSubscribing =
    whitelistUi.pending === WHITELIST_PENDING.SUBSCRIBE;

  const selectWalletWhitelistBlockReason = useMemo(() => {
    if (!isSpotTradeWhitelistRoute) return '';
    if (isPairWhitelistFetching) {
      return 'Checking whether wallet addresses are available for this trading pair...';
    }
    if (whitelistUi.pair.errorMessage) return whitelistUi.pair.errorMessage;
    if (!whitelistUi.pair.addressesReady) {
      return 'Wallet addresses are not available for this trading pair yet.';
    }
    return '';
  }, [
    isSpotTradeWhitelistRoute,
    isPairWhitelistFetching,
    whitelistUi.pair.addressesReady,
    whitelistUi.pair.errorMessage,
  ]);

  const isSelectWalletBlockedByWhitelist = useMemo(() => {
    if (!isSpotTradeWhitelistRoute) return false;
    if (isPairWhitelistFetching) return true;
    if (whitelistUi.pair.errorMessage) return true;
    return !whitelistUi.pair.addressesReady;
  }, [
    isSpotTradeWhitelistRoute,
    isPairWhitelistFetching,
    whitelistUi.pair.addressesReady,
    whitelistUi.pair.errorMessage,
  ]);

  const buyActionLoading =
    isSpotMode &&
    selectedSide === 0 &&
    (isPairWhitelistFetching || isWalletWhitelistFetching);

  return {
    whitelistUi,
    whitelistCoin,
    isSpotTradeWhitelistRoute,
    isPairWhitelistFetching,
    isWalletWhitelistFetching,
    isWhitelistSubscribing,
    selectWalletWhitelistBlockReason,
    isSelectWalletBlockedByWhitelist,
    buyActionLoading,
    walletWhitelistStatus: whitelistUi.wallet.status,
    handleSelectWallet,
    handleSubscribeWhitelist,
  };
}

export default useSpotWhitelist;
